import React, { Component } from 'react';
import { ENTER } from 'core/util/keyCodes';
import { Box, Button, Icon } from 'core/components';
import { TextArea } from 'core/form';
import styled from 'styled-components';
import { inject, observer } from 'mobx-react';
import { PROMPT_LENGTH_LIMIT } from '@kentik/ui-shared/journeys/constants';
import { PiSparkleFill } from 'react-icons/pi';
import ThinkingStream from './components/ThinkingStream';
import PendingRequestHint from './components/PendingRequestHint';

const Wrapper = styled(Box)`
  margin-bottom: 16px;
  width: 100%;
  max-width: 825px;
`;

const InputArea = styled(Box)`
  position: relative;

  .bp4-icon.input-sparkle {
    left: 16px;
    top: 18px;
    position: absolute;
    font-size: 16px;
  }
`;

const WrappedInput = styled(TextArea)`
  &.bp4-input {
    padding-top: 16px;

    // make room for absolutely positioned button
    padding-right: 50px;
    padding-bottom: 16px;
    padding-left: 46px;
    border-radius: 10px;
  }
`;

const SendButton = styled(Button)`
  position: absolute;
  top: 9px;
  right: 10px;
  background: rgba(45, 114, 210, 0.1);
  min-width: 36px !important;
  min-height: 36px !important;
  width: 36px;
  border-radius: 30px;
  padding: 0;
`;

@inject('$auth')
@observer
class JourneyInput extends Component {
  state = {
    value: ''
  };

  componentDidMount() {
    const { prompt } = this.props;
    this.setState({ value: prompt });
  }

  onKeyDown = (e) => {
    const { value } = this.state;

    const key = e.keyCode ? e.keyCode : e.which;
    const isShiftPressed = e.shiftKey;
    const hasValue = value?.length > 0;

    // user presses ENTER without shift, and the input is empty, don't submit, don't make a new line
    if (key === ENTER && !isShiftPressed && !hasValue) {
      e.preventDefault();
      return;
    }

    // user presses ENTER with shift, add a new line
    if (key === ENTER && isShiftPressed) {
      return;
    }

    // - shift + enter does not submit, it makes a new line
    // - enter without shift submits
    // - this is the same behavior as most common apps, ChatGPT, Slack, etc.
    // while the agent works, the send button is a stop button, so Enter doesn't send either
    if (key === ENTER && !isShiftPressed && hasValue && !this.isAgentStoppable()) {
      e.preventDefault();
      this.handleSendPrompt();
    }
  };

  handleChange = (e) => {
    e.preventDefault();
    this.setState({ value: e.target.value });
  };

  isApprovalPending = () => {
    const { chatSessionModel } = this.props;
    return chatSessionModel?.isAnswerPaused ?? false;
  };

  handleCancel = () => {
    const { chatSessionModel } = this.props;
    return chatSessionModel.cancelWaitingTurn();
  };

  // the agent is working on a turn the user could stop, rather than waiting on an approval
  isAgentStoppable = () => {
    const { chatSessionModel } = this.props;
    return Boolean(chatSessionModel?.isAgentActive) && !this.isApprovalPending();
  };

  handleStop = () => {
    const { chatSessionModel } = this.props;
    chatSessionModel.stopAgentTurn();
  };

  handleSendPrompt = () => {
    const { createJourney, chatSessionModel, selectJourneyAfterCreation, sessionFeatures } = this.props;
    const { value } = this.state;
    if (this.isApprovalPending()) {
      return;
    }
    this.setState({ value: '' });
    const prompt = String(value).trim().substring(0, PROMPT_LENGTH_LIMIT);
    if (!chatSessionModel?.id) {
      const newModel = createJourney();
      const features = sessionFeatures?.length > 0 ? [...sessionFeatures] : undefined;
      newModel.startChatSessionAndAddPrompt(prompt, undefined, undefined, features).catch(() => {
        // Restore the prompt value if the operation fails
        this.setState({ value: prompt });
      });
      selectJourneyAfterCreation(newModel);
    } else {
      chatSessionModel.addPrompt(prompt).catch(() => {
        // Restore the prompt value if the operation fails
        this.setState({ value: prompt });
      });
    }
  };

  render() {
    const { value } = this.state;
    const { placeholder, inputRef, chatSessionModel } = this.props;

    const approvalPending = this.isApprovalPending();

    return (
      <>
        {approvalPending && (
          <PendingRequestHint
            awaitingResponse={chatSessionModel.hasToolAwaitingApproval}
            onCancel={chatSessionModel.waitingTurn ? this.handleCancel : undefined}
          />
        )}
        <Wrapper>
          <ThinkingStream chatSessionModel={chatSessionModel} />
          <InputArea>
            <Icon className="input-sparkle" icon={PiSparkleFill} color="warning" iconSize={20} />
            <WrappedInput
              fill
              autoFocus={!approvalPending}
              autoGrow
              rows={1}
              className="jumbo"
              inputRef={inputRef}
              onChange={this.handleChange}
              placeholder={placeholder}
              value={value}
              onKeyDown={this.onKeyDown}
            />
            {this.isAgentStoppable() ? (
              <SendButton
                onClick={this.handleStop}
                disabled={!chatSessionModel.canStopAgentTurn}
                intent="primary"
                icon="stop"
                minimal
                ariaLabel="Stop response"
              />
            ) : (
              <SendButton
                onClick={this.handleSendPrompt}
                disabled={!value?.length || approvalPending}
                intent="primary"
                icon="send-message"
                minimal
                ariaLabel="Call Query"
              />
            )}
          </InputArea>
        </Wrapper>
      </>
    );
  }
}

export default JourneyInput;
